// OpenAlex API client.
//
// Endpoint: https://api.openalex.org/works?search=<keywords>&filter=...&per-page=25
// Polite-pool: pass `mailto` query param (optional, from settings).

import { fetchJson } from './http';
import type { OpenAlexWork, Result } from './types';

const BASE = 'https://api.openalex.org/works';

export interface OpenAlexOptions {
  mailto?: string;
  /** Page size; OpenAlex caps at 200. Default 25. */
  perPage?: number;
  /** Lower bound on publication year. Default: 5 years before current. */
  fromYear?: number;
  /** Optional comma-separated extra filter expression. */
  filterExtra?: string;
}

interface RawAuthorship {
  author?: {
    id?: string;
    display_name?: string;
    orcid?: string | null;
  };
  raw_affiliation_string?: string | null;
  institutions?: Array<{
    display_name?: string;
    ror?: string | null;
  }>;
}

interface RawWork {
  id?: string;
  doi?: string | null;
  title?: string;
  publication_year?: number | null;
  authorships?: RawAuthorship[];
  concepts?: Array<{ display_name?: string; level?: number }>;
}

interface RawEnvelope {
  results?: RawWork[];
  meta?: { count?: number };
}

function parseWork(raw: RawWork): OpenAlexWork {
  const id = raw.id ?? '';
  const doi = raw.doi ? raw.doi.replace(/^https?:\/\/doi\.org\//, '') : null;
  return {
    id,
    doi,
    title: raw.title ?? '',
    year: raw.publication_year ?? null,
    authors: (raw.authorships ?? []).map((a) => {
      const inst = (a.institutions ?? [])[0];
      return {
        id: a.author?.id ?? null,
        displayName: a.author?.display_name ?? '',
        orcid: a.author?.orcid ?? null,
        affiliation: inst?.display_name ?? a.raw_affiliation_string ?? null,
        affiliationRor: inst?.ror ?? null,
      };
    }),
    concepts: (raw.concepts ?? [])
      .filter((c) => (c.level ?? 99) <= 2 && typeof c.display_name === 'string')
      .map((c) => c.display_name!)
      .slice(0, 8),
  };
}

export async function searchWorks(
  query: string,
  opts: OpenAlexOptions = {},
): Promise<Result<OpenAlexWork[]>> {
  const perPage = Math.min(Math.max(opts.perPage ?? 25, 1), 200);
  const fromYear = opts.fromYear ?? new Date().getFullYear() - 5;
  const filterParts = [`from_publication_date:${fromYear}-01-01`, 'type:article'];
  if (opts.filterExtra) filterParts.push(opts.filterExtra);

  const url = new URL(BASE);
  url.searchParams.set('search', query);
  url.searchParams.set('filter', filterParts.join(','));
  url.searchParams.set('per-page', String(perPage));
  if (opts.mailto) url.searchParams.set('mailto', opts.mailto);

  const res = await fetchJson(url.toString());
  if (!res.ok) return res;
  const env = res.data as RawEnvelope;
  if (!Array.isArray(env.results)) {
    return {
      ok: false,
      degraded: true,
      error: { kind: 'invalid_response', host: 'api.openalex.org', message: 'no results array' },
    };
  }
  return { ok: true, data: env.results.map(parseWork) };
}
